import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Terminal from './components/Terminal';
import ClassicPage from './components/ClassicPage';
import Services from './components/Services';
import HomePage from './components/HomePage';
import ThemeSelector from './components/ThemeSelector';
import LanguageSelector from './components/LanguageSelector';
import SEO from './components/SEO';
import { useThemeStore } from './store/themeStore';
import { applyTheme } from './utils/terminalUtils';
import GlobalStyles from './styles/GlobalStyles';
import './i18n';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
`;

const PageWrapper = styled.div<{ $transitioning: boolean }>`
  opacity: ${({ $transitioning }) => ($transitioning ? 0 : 1)};
  transform: scale(${({ $transitioning }) => ($transitioning ? 0.98 : 1)});
  transition: opacity 0.25s ease, transform 0.25s ease;
  width: 100%;
  min-height: 100%;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

// Sayfa modları için tip tanımı
type PageMode = 'home' | 'terminal' | 'classic' | 'services';

const App: React.FC = () => {
  const { theme } = useThemeStore();
  const [pageMode, setPageMode] = useState<PageMode>('home');
  const [previousMode, setPreviousMode] = useState<PageMode>('home');
  
  // Terminal içeriğini saklamak için state'ler
  const [terminalOutput, setTerminalOutput] = useState<JSX.Element[]>([]);
  const [terminalIpAddress, setTerminalIpAddress] = useState<string>('');
  const [terminalInitialized, setTerminalInitialized] = useState<boolean>(false);
  
  // Önceki sayfa modunu saklama (terminal açılmadan önce hangi sayfadaydık)
  const [previousPageMode, setPreviousPageMode] = useState<PageMode>('home');
  const [transitioning, setTransitioning] = useState(false);
  const pendingModeRef = useRef<PageMode | null>(null);

  const switchPage = (newMode: PageMode) => {
    setTransitioning(true);
    pendingModeRef.current = newMode;
    setTimeout(() => {
      setPageMode(newMode);
      pendingModeRef.current = null;
      setTimeout(() => setTransitioning(false), 50);
    }, 250);
  };

  // URL'den sayfa modunu belirle
  const getPageModeFromURL = (): PageMode => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const page = urlParams.get('page');

    if (mode === 'classic' || page === 'classic') return 'classic';
    if (mode === 'services' || page === 'services') return 'services';
    if (mode === 'terminal' || page === 'terminal') return 'terminal';
    return 'home';
  };

  // URL parametrelerinden sayfa tercihini al
  useEffect(() => {
    setPageMode(getPageModeFromURL());
  }, []);

  // Tarayıcı geri/ileri tuşu desteği
  useEffect(() => {
    const handlePopState = () => {
      setPageMode(getPageModeFromURL());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Tema değişikliklerini uygula
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  
  // Klasik sayfaya geçiş
  const handleSwitchToClassic = () => {
    setPreviousMode(pageMode);
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'classic');
    window.history.pushState({}, '', url);
    switchPage('classic');
  };

  // Terminal'e geçiş
  const handleSwitchToTerminal = () => {
    setPreviousPageMode(pageMode);
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'terminal');
    url.hash = '';
    window.history.pushState({}, '', url);
    switchPage('terminal');
  };

  // Hizmetler sayfasına geçiş
  const handleSwitchToServices = () => {
    setPreviousMode(pageMode);
    const url = new URL(window.location.href);
    url.searchParams.set('mode', 'services');
    url.hash = '';
    window.history.pushState({}, '', url);
    switchPage('services');
  };

  // Ana sayfaya geçiş
  const handleSwitchToHome = () => {
    setPreviousMode(pageMode);
    const url = new URL(window.location.href);
    url.searchParams.delete('mode');
    url.hash = '';
    window.history.pushState({}, '', url);
    switchPage('home');
  };

  // Terminal'den çıkış - önceki sayfaya dön
  const handleExitTerminal = () => {
    if (previousPageMode === 'home') {
      const url = new URL(window.location.href);
      url.searchParams.delete('mode');
      url.hash = '';
      window.history.pushState({}, '', url);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.set('mode', previousPageMode);
      window.history.pushState({}, '', url);
    }
    switchPage(previousPageMode);
  };

  // Önceki sayfaya dön
  const handleGoBack = () => {
    if (previousMode === 'home') {
      const url = new URL(window.location.href);
      url.searchParams.delete('mode');
      url.hash = '';
      window.history.pushState({}, '', url);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.set('mode', previousMode);
      url.hash = '';
      window.history.pushState({}, '', url);
    }
    switchPage(previousMode);
  };
  
  // SEO başlığını belirleme
  const getSEOTitle = () => {
    switch (pageMode) {
      case 'classic':
        return 'Portfolio';
      case 'services':
        return 'Danışmanlık & Freelance Hizmetleri';
      case 'terminal':
        return 'Terminal';
      default:
        return 'Yazılım Geliştirici & Çözüm Mimarı';
    }
  };
  
  // SEO şema tipini belirleme
  const getSEOSchemaType = () => {
    switch (pageMode) {
      case 'classic':
        return 'ProfilePage';
      case 'services':
        return 'Service';
      case 'terminal':
        return 'WebApplication';
      default:
        return 'WebSite';
    }
  };
  
  return (
    <>
    <GlobalStyles />
    <PageWrapper $transitioning={transitioning}>
    <div className={`app ${pageMode !== 'terminal' && pageMode !== 'home' ? 'classic-mode' : ''}`}>
      <SEO
        title={getSEOTitle()}
        schemaType={getSEOSchemaType()}
      />

      {pageMode !== 'home' && <ThemeSelector />}
      {pageMode !== 'home' && <LanguageSelector />}

      {pageMode === 'home' && (
        <HomePage
          onOpenTerminal={handleSwitchToTerminal}
          onOpenServices={handleSwitchToServices}
        />
      )}

      {pageMode === 'classic' && (
        <ClassicPage
          onSwitchToTerminal={handleSwitchToTerminal}
          onSwitchToServices={handleSwitchToServices}
        />
      )}

      {pageMode === 'terminal' && (
        <Terminal
          onSwitchToClassic={handleSwitchToClassic}
          onSwitchToServices={handleSwitchToServices}
          onSwitchToHome={handleSwitchToHome}
          onExitTerminal={handleExitTerminal}
          output={terminalOutput}
          setOutput={setTerminalOutput}
          ipAddress={terminalIpAddress}
          setIpAddress={setTerminalIpAddress}
          isInitialized={terminalInitialized}
          setIsInitialized={setTerminalInitialized}
        />
      )}

      {pageMode === 'services' && (
        <Services
          onBack={handleGoBack}
          onSwitchToTerminal={handleSwitchToTerminal}
          onSwitchToClassic={handleSwitchToClassic}
          onSwitchToHome={handleSwitchToHome}
        />
      )}
    </div>
    </PageWrapper>
    </>
  );
};

export default App; 